import type {
  CellClassParams,
  CellClassRules,
  ExcelStyle
} from "ag-grid-enterprise";
import { excelColor } from "components/excel-ag-grid-export/excel-style";
import { getMatchingConditionalFormattingActions } from "./apply-format-rules";
import type {
  ConditionalFormattingActions,
  ConditionalFormattingRule
} from "./types";

export const CF_TEXT_COLOR_CELL_CLASS = "cf-text-color";
export const CF_FONT_WEIGHT_CELL_CLASS = "cf-font-weight";
export const CF_FONT_STYLE_CELL_CLASS = "cf-font-style";

const sanitizeStyleIdPart = (value?: string | number | boolean | null) =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 24);

const getMatchedFormattingActions = <TData>(
  params: CellClassParams<TData>,
  rules: ConditionalFormattingRule[]
) => {
  const columnId =
    params.column?.getColId?.() || params.colDef.colId || params.colDef.field;
  return getMatchingConditionalFormattingActions({
    columnId,
    value: params.value,
    rules,
    node: params.node
  });
};

const isCssVariable = (value?: string) => value?.startsWith("var(") ?? false;

export const getConditionalFormattingExcelStyleId = (
  actions: ConditionalFormattingActions
): string =>
  [
    "cf",
    sanitizeStyleIdPart(actions.backgroundColor),
    sanitizeStyleIdPart(actions.textColor),
    actions.bold ? "b" : "",
    actions.italic ? "i" : "",
    actions.underline ? "u" : "",
    actions.strike ? "s" : "",
    sanitizeStyleIdPart(actions.fontSize)
  ]
    .filter(Boolean)
    .join("_");

export const conditionalFormattingActionsToExcelStyle = (
  actions: ConditionalFormattingActions
): ExcelStyle | null => {
  const hasVisualStyle =
    (!!actions.backgroundColor && !isCssVariable(actions.backgroundColor)) ||
    (!!actions.textColor && !isCssVariable(actions.textColor)) ||
    !!actions.bold ||
    !!actions.italic ||
    !!actions.underline ||
    !!actions.strike ||
    !!actions.fontSize;
  if (!hasVisualStyle) return null;

  const style: ExcelStyle = {
    id: getConditionalFormattingExcelStyleId(actions),
    alignment: {
      vertical: "Top"
    }
  };

  if (actions.backgroundColor && !isCssVariable(actions.backgroundColor)) {
    // Match header fills: #RRGGBB (AG Grid convertLegacyColor adds FF).
    style.interior = {
      color: actions.backgroundColor,
      pattern: "Solid"
    };
  }

  const font: NonNullable<ExcelStyle["font"]> = {};
  if (actions.textColor && !isCssVariable(actions.textColor)) {
    font.color = excelColor(actions.textColor);
  }
  if (actions.bold) font.bold = true;
  if (actions.italic) font.italic = true;
  if (actions.underline) font.underline = "Single";
  if (actions.strike) font.strikeThrough = true;
  if (actions.fontSize) font.size = actions.fontSize;
  if (Object.keys(font).length) style.font = font;

  return style;
};

export const buildConditionalFormattingExcelStyles = (
  rules: ConditionalFormattingRule[]
): ExcelStyle[] => {
  const byId = new Map<string, ExcelStyle>();
  rules.forEach((rule) => {
    [
      rule.defaultActions,
      ...rule.conditions.map(({ actions }) => actions)
    ].forEach((actions) => {
      const style = conditionalFormattingActionsToExcelStyle(actions ?? {});
      if (style) byId.set(style.id, style);
    });
  });
  return Array.from(byId.values());
};

export const buildConditionalFormattingCellClassRules = <TData>(
  rules: ConditionalFormattingRule[],
  styleIds: string[]
): CellClassRules<TData> => {
  const cellClassRules: CellClassRules<TData> = {};
  styleIds.forEach((styleId) => {
    cellClassRules[styleId] = (params: CellClassParams<TData>) => {
      const matchedActions = getMatchedFormattingActions(params, rules);
      return (
        !!matchedActions &&
        getConditionalFormattingExcelStyleId(matchedActions) === styleId
      );
    };
  });
  return cellClassRules;
};

/** Marker classes so child link styles inherit CF color / weight / italic. */
export const buildConditionalFormattingInheritCellClassRules = <TData>(
  rules: ConditionalFormattingRule[]
): CellClassRules<TData> => ({
  [CF_TEXT_COLOR_CELL_CLASS]: (params: CellClassParams<TData>) =>
    !!getMatchedFormattingActions(params, rules)?.textColor,
  [CF_FONT_WEIGHT_CELL_CLASS]: (params: CellClassParams<TData>) =>
    !!getMatchedFormattingActions(params, rules)?.bold,
  [CF_FONT_STYLE_CELL_CLASS]: (params: CellClassParams<TData>) =>
    !!getMatchedFormattingActions(params, rules)?.italic
});

export const mergeExcelStylesWithConditionalFormatting = (
  baseStyles: ExcelStyle[],
  rules: ConditionalFormattingRule[]
): ExcelStyle[] => [
  ...baseStyles,
  ...buildConditionalFormattingExcelStyles(rules)
];
